/**
 * Case DETAIL's loading shapes — the `$id` route's per-section `Suspense`
 * fallbacks (`DetailsTitle` / `DetailsHeaderCluster` / `DetailsTabs`) and the
 * page-level `pendingComponent` that composes them. Read each next to the
 * component it mirrors in this folder.
 *
 * Not in `../skeletons.tsx` with the Case list shapes, because of weight (P-21):
 * `CaseDetailsSkeleton` draws the AI rail, and the multi-tenant Cases tab
 * imports the list shapes eagerly. One file would ship the rail to that route.
 */
import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { useCasePageContext } from '@/features/air/case-management/ai/hooks/use-case-page-context'
import { Section } from '@/features/app-shell/section'

import { Skeleton } from '@/components/ui/skeleton'

/**
 * Mirrors `DetailsTitle`: back button, title + description stack, and the
 * status-select/ask-AI cluster pinned to the end via the same `flex-auto`
 * spacer the real component uses.
 */
export const DetailsTitleSkeleton = () => (
	<div className="flex w-full flex-col items-start justify-start gap-4 sm:flex-row">
		<Skeleton className="h-10 w-10 shrink-0 rounded-md" />

		<div className="flex w-full flex-col items-start justify-start gap-2">
			<Skeleton className="h-7 w-64" />
			<Skeleton className="h-4 w-96" />
		</div>

		<div className="flex-auto" />

		<div className="flex items-center gap-2">
			<Skeleton className="h-9 w-32" />
			<Skeleton className="h-9 w-24" />
		</div>
	</div>
)

/**
 * Mirrors `DetailsHeaderCluster`: the status pill, severity pill, and the
 * four icon-label fields, wrapping on the same `flex-wrap` row.
 */
export const DetailsHeaderClusterSkeleton = () => (
	<div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2">
		<Skeleton className="h-6 w-20 rounded-full" />
		<Skeleton className="h-6 w-16 rounded-full" />
		<Skeleton className="h-5 w-24" />
		<Skeleton className="h-5 w-28" />
		<Skeleton className="h-5 w-32" />
		<Skeleton className="h-5 w-36" />
	</div>
)

/**
 * Mirrors `DetailsTabs`'s `h-10` tab strip — bar count matches the
 * SKU-on tab set (Behavioral Insights, Case Details, Evidence, Investigation
 * Steps, Automation, Logs, Case Notes); SKU-off cases render fewer real tabs,
 * so this is the upper bound, not an exact count.
 */
export const DetailsTabsSkeleton = () => (
	<div className="flex h-10 max-w-full items-start justify-start gap-6 overflow-x-auto overflow-y-hidden">
		<Skeleton className="h-8 w-28" />
		<Skeleton className="h-8 w-24" />
		<Skeleton className="h-8 w-20" />
		<Skeleton className="h-8 w-28" />
		<Skeleton className="h-8 w-24" />
		<Skeleton className="h-8 w-16" />
		<Skeleton className="h-8 w-20" />
	</div>
)

/**
 * The `$id` route's `pendingComponent`. `Outlet` (the tab's own nested route,
 * which has its own pending state) and `LogPanelMount` (a `null` Suspense
 * fallback — nothing to draw) render above the fold in `RouteComponent` only,
 * so they aren't part of this shape.
 *
 * `AIRailLayout` wraps this for the same reason the real page's wrapper is part
 * of every other composite: the rail is static chrome that needs no server
 * response, so drawing it only after the case resolves makes it pop in and
 * re-flows the content column. `useCasePageContext` reads route params only, so
 * it resolves during the pending state without suspending.
 */
export const CaseDetailsSkeleton = () => {
	const pageContext = useCasePageContext()

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section>
				<div className="bg-background flex flex-col gap-4 pt-2 pb-3">
					<DetailsTitleSkeleton />
					<DetailsHeaderClusterSkeleton />
					<DetailsTabsSkeleton />
				</div>
			</Section>
		</AIRailLayout>
	)
}
